import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Reveal from "../components/Reveal";
import { ARTICLES } from "../data/articles";

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | SERENATTI`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="pt-40 pb-24 px-6 md:px-12 text-center">
        <h1 className="font-heading text-3xl text-navy">Article not found</h1>
        <button
          onClick={() => navigate("/learn")}
          className="mt-6 inline-flex items-center gap-2 text-navy hover:text-gold text-xs tracking-[0.22em] uppercase"
        >
          <ArrowLeft size={14} /> Back to Learn
        </button>
      </div>
    );
  }

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`page-article-${article.slug}`} className="pt-32 md:pt-40 pb-24">
      <article className="px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/learn"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-navy hover:text-gold mb-8"
          >
            <ArrowLeft size={14} /> Back to Learn
          </Link>

          <Reveal>
            <div className="flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase">
              <span className="text-gold">{article.category}</span>
              <span className="text-charcoal/40">&middot;</span>
              <span className="text-charcoal/60">{article.readTime}</span>
            </div>
            <h1 className="mt-4 font-heading text-4xl md:text-6xl text-navy leading-[1.05] tracking-tight text-balance">
              {article.title}
            </h1>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-10 aspect-[16/9] overflow-hidden rounded-sm bg-navy/5">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <Reveal delay={200}>
            <p className="mt-10 text-lg md:text-xl text-charcoal leading-relaxed font-heading italic">
              {article.intro}
            </p>
          </Reveal>

          <div className="mt-10 space-y-10">
            {article.body.map((sec, i) => (
              <Reveal key={i} delay={i * 60}>
                <section data-testid={`article-section-${i}`}>
                  <h2 className="font-heading text-2xl md:text-3xl text-navy leading-tight">{sec.heading}</h2>
                  <p className="mt-4 text-base md:text-lg text-charcoal/85 leading-relaxed">{sec.text}</p>
                </section>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <section data-testid="article-sources" className="mt-16 pt-10 border-t border-navy/15">
              <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-4">Sources</p>
              <ol className="space-y-3 text-sm text-charcoal/80 list-decimal list-inside">
                {article.sources.map((s, i) => (
                  <li key={i} className="leading-relaxed">
                    {s.text}{" "}
                    {s.url && (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy hover:text-gold underline underline-offset-2 break-all"
                      >
                        {s.url.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                  </li>
                ))}
              </ol>
              <p data-testid="article-sources-note" className="mt-6 pt-6 border-t border-navy/10 text-xs text-charcoal/60 leading-relaxed italic">
                Every source cited on this site has been verified to exist and to say what this article reports. If you find an error, please tell me &mdash; corrections are published, not hidden.
              </p>
            </section>
          </Reveal>

          <Reveal>
            <section className="mt-16 p-6 bg-sage/15 border-l-2 border-gold rounded-sm">
              <p className="text-sm text-charcoal/85 leading-relaxed">
                <strong className="text-navy">Educational information only.</strong> This article does not
                provide medical advice, diagnosis, or treatment. Always consult your own healthcare provider
                for questions about your health.
              </p>
            </section>
          </Reveal>
        </div>
      </article>

      {others.length > 0 && (
        <section className="mt-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-4">Keep reading</p>
            <h3 className="font-heading text-2xl md:text-3xl text-navy mb-10">More from the library</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {others.map((a) => (
                <Link
                  key={a.slug}
                  to={`/learn/${a.slug}`}
                  data-testid={`article-related-${a.slug}`}
                  className="group bg-white border border-navy/10 rounded-sm overflow-hidden hover-lift"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-gold">{a.category}</p>
                    <h4 className="mt-2 font-heading text-xl text-navy group-hover:text-gold transition-colors">{a.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
